/**
 * SaveSlotPersistence — save-slot data stored in localStorage.
 *
 * Three slots (0–2). Each stores character metadata and a scene checkpoint.
 * Pattern mirrors SettingsPersistence.ts — graceful fallback on corrupt data.
 *
 * Painted-scene journey state (active scene key, walker pos, interaction
 * flags) lives in a sibling namespace `painted_scene_state_<slotIndex>` and
 * is written by `SceneRouter`. `deleteSlot()` clears both so erasing a slot
 * wipes its journey entirely.
 */

import { sceneRouterStorageKeyForSlot } from '../scenes/painted/SceneRouter';

const SLOT_KEY_PREFIX = 'save_slot_';
const SLOT_COUNT = 3;

export interface SaveSlotData {
  characterName: string;
  vocation: string;
  playtimeSeconds: number;
  lastPlayedISO: string;
  sceneCheckpoint: string;
}

export function getSlotCount(): number {
  return SLOT_COUNT;
}

export function loadSlot(index: number): SaveSlotData | null {
  if (index < 0 || index >= SLOT_COUNT) return null;
  try {
    const raw = localStorage.getItem(`${SLOT_KEY_PREFIX}${index}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed.characterName !== 'string' ||
      typeof parsed.vocation !== 'string' ||
      typeof parsed.playtimeSeconds !== 'number' ||
      typeof parsed.lastPlayedISO !== 'string' ||
      typeof parsed.sceneCheckpoint !== 'string'
    ) {
      return null;
    }
    return parsed as SaveSlotData;
  } catch {
    return null;
  }
}

export function saveSlot(index: number, data: SaveSlotData): void {
  if (index < 0 || index >= SLOT_COUNT) return;
  try {
    localStorage.setItem(`${SLOT_KEY_PREFIX}${index}`, JSON.stringify(data));
  } catch {
    // localStorage unavailable
  }
}

export function deleteSlot(index: number): void {
  if (index < 0 || index >= SLOT_COUNT) return;
  try {
    localStorage.removeItem(`${SLOT_KEY_PREFIX}${index}`);
    localStorage.removeItem(sceneRouterStorageKeyForSlot(index));
  } catch {
    // localStorage unavailable
  }
}

/**
 * Storage key that `SceneRouter` should use for this slot. Title screen wires
 * the router with this key so each slot's journey state stays isolated.
 */
export function sceneRouterStorageKey(index: number): string {
  return sceneRouterStorageKeyForSlot(index);
}

export function loadAllSlots(): Array<SaveSlotData | null> {
  const slots: Array<SaveSlotData | null> = [];
  for (let i = 0; i < SLOT_COUNT; i++) {
    slots.push(loadSlot(i));
  }
  return slots;
}

/** Format playtime seconds as "Xh Ym" or "Ym" if under an hour. */
export function formatPlaytime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/** Format ISO date as a short human-readable string. */
export function formatLastPlayed(iso: string): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return d.toLocaleDateString();
  } catch {
    return '';
  }
}
